/**
 * HERCULES Mobile Menu
 * Dosya: assets/hercules-mobile-menu.js
 *
 * Özellikler:
 *  - Panel aç/kapat (overlay, buton, escape tuşu, dış alan)
 *  - Body scroll lock (iOS momentum scroll dahil)
 *  - Alt panel (submenu) sistemi
 *  - Erişilebilirlik: aria-hidden, aria-expanded, focus trap
 *  - Sepet badge güncelleme (Shopify Cart API)
 *  - Arama input otomatik focus
 *  - Performanslı: requestAnimationFrame ile sınıf geçişleri
 *  - Hata toleranslı: null kontrolleri her yerde
 */

(function () {
  'use strict';

  // ─── Sabitler ────────────────────────────────────────────────────────────────
  var SELECTORS = {
    trigger: '[data-hrc-menu-trigger]',
    panel: '#hrcMenuPanel',
    overlay: '#hrcMenuOverlay',
    closeBtn: '[data-hrc-menu-close]',
    subTrigger: '[data-submenu]',
    backBtn: '[data-parent]',
    cartBadge: '#hrcCartBadge',
    searchInput: '.hrc-menu__search-input',
    scroll: '#hrcMenuScroll',
  };

  var CSS = {
    panelOpen: 'is-open',
    overlayVis: 'is-visible',
    overlayOpen: 'is-open',
    subOpen: 'is-open',
    bodyLock: 'hrc-menu-open',
    cartHidden: 'hrc-menu__cart-badge--hidden',
  };

  var OVERLAY_TRANSITION_MS = 320;

  var FOCUS_SELECTORS = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(', ');

  // ─── Durum ───────────────────────────────────────────────────────────────────
  var state = {
    isOpen: false,
    activeSubId: null,
    scrollY: 0,
    focusedBefore: null,
  };

  // ─── DOM referansları ────────────────────────────────────────────────────────
  var panel, overlay, cartBadge, searchInput;

  // ─── Yardımcı fonksiyonlar ───────────────────────────────────────────────────

  /**
   * Güvenli querySelector — null döndürebilir, hata fırlatmaz.
   */
  function $(selector, context) {
    try {
      return (context || document).querySelector(selector);
    } catch (_) {
      return null;
    }
  }

  /**
   * Güvenli querySelectorAll — boş NodeList döndürür, hata fırlatmaz.
   */
  function $$(selector, context) {
    try {
      return Array.prototype.slice.call((context || document).querySelectorAll(selector));
    } catch (_) {
      return [];
    }
  }

  /**
   * requestAnimationFrame kullanarak iki ardışık frame'de sınıf ekle.
   * Animasyonların tetiklenmesi için gerekli (display:none → display:block → .is-open).
   */
  function rafClass(el, cls) {
    if (!el) return;
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        el.classList.add(cls);
      });
    });
  }

  // ─── Scroll Lock ─────────────────────────────────────────────────────────────

  function lockScroll() {
    state.scrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
    document.body.classList.add(CSS.bodyLock);
    document.body.style.top = '-' + state.scrollY + 'px';
  }

  function unlockScroll() {
    document.body.classList.remove(CSS.bodyLock);
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    window.scrollTo(0, state.scrollY);
  }

  // ─── Overlay yardımcıları ────────────────────────────────────────────────────

  function hideOverlayFromLayout() {
    if (!overlay) return;
    overlay.classList.remove(CSS.overlayVis);
    overlay.setAttribute('aria-hidden', 'true');
  }

  function scheduleOverlayLayoutHide() {
    if (!overlay) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      hideOverlayFromLayout();
      return;
    }

    var fallback = setTimeout(hideOverlayFromLayout, OVERLAY_TRANSITION_MS + 50);

    function onTransitionEnd(e) {
      if (e.target !== overlay || e.propertyName !== 'opacity') return;
      clearTimeout(fallback);
      hideOverlayFromLayout();
      overlay.removeEventListener('transitionend', onTransitionEnd);
    }

    overlay.addEventListener('transitionend', onTransitionEnd);
  }

  function resetMenuDomState() {
    state.isOpen = false;
    state.activeSubId = null;

    if (panel) {
      panel.classList.remove(CSS.panelOpen);
      panel.setAttribute('aria-hidden', 'true');
    }

    if (overlay) {
      overlay.classList.remove(CSS.overlayOpen, CSS.overlayVis);
      overlay.setAttribute('aria-hidden', 'true');
    }

    closeAllSubPanels();
    unlockScroll();

    $$(SELECTORS.trigger).forEach(function (btn) {
      btn.setAttribute('aria-expanded', 'false');
    });

    document.removeEventListener('keydown', handleKeyDown);
  }

  // ─── Focus Trap ──────────────────────────────────────────────────────────────

  function getFocusableElements() {
    if (!panel) return [];
    return $$(FOCUS_SELECTORS, panel).filter(function (el) {
      return !el.closest('[aria-hidden="true"]') && el.offsetParent !== null;
    });
  }

  function handleFocusTrap(e) {
    if (!state.isOpen || e.key !== 'Tab') return;

    var focusable = getFocusableElements();
    if (focusable.length === 0) return;

    var first = focusable[0];
    var last = focusable[focusable.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  // ─── Menü Aç/Kapat ───────────────────────────────────────────────────────────

  function openMenu() {
    if (state.isOpen) return;
    state.isOpen = true;
    state.focusedBefore = document.activeElement;

    // Overlay: önce görünür yap, sonra animasyon başlasın
    if (overlay) {
      overlay.setAttribute('aria-hidden', 'false');
      overlay.classList.add(CSS.overlayVis);
      rafClass(overlay, CSS.overlayOpen);
    }

    // Panel: aria güncelle + sınıf ekle
    if (panel) {
      panel.setAttribute('aria-hidden', 'false');
      rafClass(panel, CSS.panelOpen);
    }

    lockScroll();

    // Tüm trigger butonlarına aria-expanded ekle
    $$(SELECTORS.trigger).forEach(function (btn) {
      btn.setAttribute('aria-expanded', 'true');
    });

    // Arama inputuna focus (kısa gecikme — panel animasyonu bitmeden focus çalışmaz)
    setTimeout(function () {
      if (searchInput) searchInput.focus();
    }, 320);

    document.addEventListener('keydown', handleKeyDown);
  }

  function closeMenu() {
    if (!state.isOpen) return;
    state.isOpen = false;

    // Tüm açık alt panelleri kapat
    closeAllSubPanels();

    // Overlay kapat — pointer-events anında kapanır (.is-open kalkınca)
    if (overlay) {
      overlay.classList.remove(CSS.overlayOpen);
      overlay.setAttribute('aria-hidden', 'true');
      scheduleOverlayLayoutHide();
    }

    // Panel kapat
    if (panel) {
      panel.classList.remove(CSS.panelOpen);
      panel.setAttribute('aria-hidden', 'true');
    }

    unlockScroll();

    $$(SELECTORS.trigger).forEach(function (btn) {
      btn.setAttribute('aria-expanded', 'false');
    });

    // Odağı geri ver
    if (state.focusedBefore && typeof state.focusedBefore.focus === 'function') {
      state.focusedBefore.focus();
    }

    document.removeEventListener('keydown', handleKeyDown);
  }

  function toggleMenu() {
    if (state.isOpen) {
      closeMenu();
    } else {
      openMenu();
    }
  }

  // ─── Alt Panel ───────────────────────────────────────────────────────────────

  function openSubPanel(subId) {
    var sub = $('#' + subId);
    if (!sub) return;

    // Önceki açık paneli kapat
    if (state.activeSubId && state.activeSubId !== subId) {
      closeSubPanel(state.activeSubId);
    }

    state.activeSubId = subId;
    sub.setAttribute('aria-hidden', 'false');
    sub.classList.add(CSS.subOpen);

    // Trigger butonunun aria-expanded'ını güncelle
    var trigger = $('[data-submenu="' + subId + '"]');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');

    // Alt panel başlığına focus
    setTimeout(function () {
      var firstFocusable = $(FOCUS_SELECTORS, sub);
      if (firstFocusable) firstFocusable.focus();
    }, 260);
  }

  function closeSubPanel(subId) {
    var sub = subId ? $('#' + subId) : null;
    if (!sub) return;

    sub.classList.remove(CSS.subOpen);
    sub.setAttribute('aria-hidden', 'true');

    var trigger = $('[data-submenu="' + subId + '"]');
    if (trigger) {
      trigger.setAttribute('aria-expanded', 'false');
      // Odağı ana paneldeki trigger'a ver
      setTimeout(function () {
        trigger.focus();
      }, 260);
    }

    if (state.activeSubId === subId) {
      state.activeSubId = null;
    }
  }

  function closeAllSubPanels() {
    $$('.hrc-menu__sub-panel').forEach(function (sub) {
      sub.classList.remove(CSS.subOpen);
      sub.setAttribute('aria-hidden', 'true');
    });
    $$('[data-submenu]').forEach(function (btn) {
      btn.setAttribute('aria-expanded', 'false');
    });
    state.activeSubId = null;
  }

  // ─── Klavye Yönetimi ─────────────────────────────────────────────────────────

  function handleKeyDown(e) {
    switch (e.key) {
      case 'Escape':
        // Alt panel açıksa önce onu kapat
        if (state.activeSubId) {
          closeSubPanel(state.activeSubId);
        } else {
          closeMenu();
        }
        break;

      case 'Tab':
        handleFocusTrap(e);
        break;

      default:
        break;
    }
  }

  // ─── Sepet Badge Güncelleme ───────────────────────────────────────────────────

  /**
   * Shopify Cart API'den mevcut sepet adedini çeker ve badge'i günceller.
   * Sayfa yüklendiğinde ve sepet değişikliklerinde çağrılabilir.
   */
  function updateCartBadge() {
    if (!cartBadge) return;

    fetch('/cart.js', {
      method: 'GET',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Cart fetch failed');
        return res.json();
      })
      .then(function (cart) {
        var count = cart.item_count || 0;
        cartBadge.textContent = count;

        if (count > 0) {
          cartBadge.classList.remove(CSS.cartHidden);
          var i18n = window.hrcI18n || {};
          var ariaTpl = i18n.itemsInCart || i18n.cartAria || '__COUNT__ items in your cart';
          cartBadge.setAttribute(
            'aria-label',
            ariaTpl
              .replace('__COUNT__', String(count))
              .replace('{{ count }}', String(count))
          );
        } else {
          cartBadge.classList.add(CSS.cartHidden);
        }
      })
      .catch(function (err) {
        // Hata durumunda sessizce geç
        console.warn('[HRC Menu] Cart badge update failed:', err);
      });
  }

  /**
   * Shopify'ın cart:refresh olayını dinle (Ajax cart'lar için).
   * Tema cart:refresh veya cart:updated olayı fırlatıyorsa badge otomatik güncellenir.
   */
  function listenCartEvents() {
    document.addEventListener('cart:refresh', updateCartBadge);
    document.addEventListener('cart:updated', updateCartBadge);

    // Shopify'ın native theme events (Dawn vb. temalar)
    document.addEventListener('on:cart:add', updateCartBadge);
    document.addEventListener('cart-update', updateCartBadge);
  }

  // ─── Event Listener'lar ──────────────────────────────────────────────────────

  function bindEvents() {
    // Hamburger trigger(lar)
    $$(SELECTORS.trigger).forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        toggleMenu();
      });
    });

    // Kapatma butonu
    $$(SELECTORS.closeBtn).forEach(function (btn) {
      btn.addEventListener('click', closeMenu);
    });

    // Overlay tıklama
    if (overlay) {
      overlay.addEventListener('click', closeMenu);
    }

    // Alt panel trigger'ları
    $$(SELECTORS.subTrigger).forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var subId = btn.getAttribute('data-submenu');
        if (!subId) return;

        if (state.activeSubId === subId) {
          closeSubPanel(subId);
        } else {
          openSubPanel(subId);
        }
      });
    });

    // Geri butonları
    $$(SELECTORS.backBtn).forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var parentId = btn.getAttribute('data-parent');
        if (parentId) closeSubPanel(parentId);
      });
    });

    // Panel içindeki linklere tıklayınca menüyü kapat
    if (panel) {
      panel.addEventListener('click', function (e) {
        var loginTrigger = e.target.closest('[data-lm-trigger]');
        if (loginTrigger) {
          e.preventDefault();
          closeMenu();
          return;
        }

        var link = e.target.closest('a[href]');
        if (link && !link.closest('.hrc-menu__sub-panel .hrc-menu__sub-header')) {
          closeMenu();
        }
      });
    }
  }

  // ─── Init ────────────────────────────────────────────────────────────────────

  function init() {
    panel = $(SELECTORS.panel);
    overlay = $(SELECTORS.overlay);
    cartBadge = $(SELECTORS.cartBadge);
    searchInput = $(SELECTORS.searchInput);

    // Panel yoksa menü yoktur, devam etme
    if (!panel) return;

    // Önceki oturumdan kalan kilitli state'i temizle
    resetMenuDomState();

    // Başlangıç aria durumları
    panel.setAttribute('aria-hidden', 'true');
    if (overlay) overlay.setAttribute('aria-hidden', 'true');

    $$('.hrc-menu__sub-panel').forEach(function (sub) {
      sub.setAttribute('aria-hidden', 'true');
    });

    bindEvents();
    updateCartBadge();
    listenCartEvents();

    // iOS Safari: geri tuşu / bfcache sonrası kilitli state temizliği
    window.addEventListener('pageshow', function (e) {
      if (e.persisted && !state.isOpen) {
        resetMenuDomState();
      }
    });
  }

  // ─── DOMContentLoaded ────────────────────────────────────────────────────────

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // ─── Public API (opsiyonel, tema JS'den erişim için) ─────────────────────────
  window.HerculesMenu = {
    open: openMenu,
    close: closeMenu,
    toggle: toggleMenu,
    reset: resetMenuDomState,
    updateCartBadge: updateCartBadge,
  };
})();
